import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, FileText, X, CheckCircle, AlertCircle, File } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';

const AFAFileUpload = ({ maxFiles, onFilesChange, onContinue }) => {
  const [files, setFiles] = useState([]);

  const onDrop = useCallback(
    (acceptedFiles, rejectedFiles) => {
      // Handle rejected files
      if (rejectedFiles.length > 0) {
        rejectedFiles.forEach(({ file, errors }) => {
          errors.forEach((error) => {
            if (error.code === 'file-too-large') {
              toast.error(`${file.name} er for stor. Maks 10MB per fil.`);
            } else if (error.code === 'file-invalid-type') {
              toast.error(`${file.name} er ikke et støttet filformat.`);
            } else {
              toast.error(`Kunne ikke laste opp ${file.name}`);
            }
          });
        });
      }

      // Check if adding these files exceeds limit
      if (files.length + acceptedFiles.length > maxFiles) {
        toast.error(`Du kan kun laste opp ${maxFiles} dokument${maxFiles > 1 ? 'er' : ''}.`);
        return;
      }

      // Add accepted files
      const newFiles = acceptedFiles.map((file) => ({
        file,
        id: Math.random().toString(36).substr(2, 9),
        name: file.name,
        size: file.size,
        type: file.type,
        preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : null
      }));

      const updatedFiles = [...files, ...newFiles];
      setFiles(updatedFiles);
      
      if (onFilesChange) {
        onFilesChange(updatedFiles);
      }

      toast.success(`${acceptedFiles.length} fil${acceptedFiles.length > 1 ? 'er' : ''} lastet opp!`);
    },
    [files, maxFiles, onFilesChange]
  );

  const removeFile = (fileId) => {
    const updatedFiles = files.filter((f) => f.id !== fileId);
    setFiles(updatedFiles);
    
    if (onFilesChange) {
      onFilesChange(updatedFiles);
    }
    
    toast.info('Fil fjernet');
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
      'text/plain': ['.txt']
    },
    maxSize: 10 * 1024 * 1024, // 10MB
    multiple: maxFiles > 1,
    disabled: files.length >= maxFiles
  });

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const canAddMore = files.length < maxFiles;

  return (
    <div className="w-full max-w-3xl mx-auto p-6 bg-white rounded-lg border border-gray-200 shadow-sm">
      {/* Header */}
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-1">
          Last opp dokumenter
        </h3>
        <p className="text-sm text-gray-600">
          Du kan laste opp opptil {maxFiles} dokument{maxFiles > 1 ? 'er' : ''} ({files.length}/{maxFiles})
        </p>
      </div>

      {/* Dropzone */}
      {canAddMore && (
        <div
          {...getRootProps()}
          className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-all ${
            isDragActive
              ? 'border-gray-900 bg-gray-50'
              : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
          }`}
        >
          <input {...getInputProps()} />
          <Upload className={`w-12 h-12 mx-auto mb-3 ${isDragActive ? 'text-gray-900' : 'text-gray-400'}`} />
          
          {isDragActive ? (
            <p className="text-gray-900 font-medium">Slipp filene her...</p>
          ) : (
            <>
              <p className="text-gray-900 font-medium mb-1">
                Klikk for å velge eller dra og slipp filer her
              </p>
              <p className="text-sm text-gray-500">
                PDF, Word, bilder • Maks 10MB per fil
              </p>
            </>
          )}
        </div>
      )}

      {/* File List */}
      {files.length > 0 && (
        <div className="mt-4 space-y-2">
          {files.map((file) => (
            <div
              key={file.id}
              className="flex items-center gap-3 p-3 bg-gray-50 border border-gray-200 rounded-lg"
            >
              {/* File Icon */}
              <div className="w-10 h-10 bg-gray-100 rounded flex items-center justify-center flex-shrink-0">
                {file.type === 'application/pdf' ? (
                  <FileText className="w-5 h-5 text-red-600" />
                ) : file.type.includes('word') ? (
                  <FileText className="w-5 h-5 text-blue-600" />
                ) : file.type.startsWith('image/') ? (
                  <File className="w-5 h-5 text-green-600" />
                ) : (
                  <File className="w-5 h-5 text-gray-600" />
                )}
              </div>

              {/* File Info */}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {file.name}
                </p>
                <p className="text-xs text-gray-500">
                  {formatFileSize(file.size)}
                </p>
              </div>

              {/* Success Icon */}
              <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />

              {/* Remove Button */}
              <button
                onClick={() => removeFile(file.id)}
                className="p-1 hover:bg-gray-200 rounded transition-colors"
                title="Fjern fil"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Info Message */}
      {files.length > 0 && canAddMore && (
        <div className="mt-4 flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-blue-900">
            Du kan laste opp {maxFiles - files.length} dokument{maxFiles - files.length > 1 ? 'er' : ''} til.
          </p>
        </div>
      )}

      {/* Continue Button */}
      <div className="mt-6 flex gap-3">
        <Button
          onClick={() => onContinue && onContinue()}
          className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white"
        >
          {files.length > 0 ? 'Fortsett med disse dokumentene' : 'Fortsett uten dokumenter'}
        </Button>
      </div>

      {/* File Format Info */}
      <div className="mt-4 pt-4 border-t border-gray-200">
        <p className="text-xs text-gray-500 text-center">
          Støttede formater: PDF, Word (.doc, .docx), bilder (JPG, PNG), tekstfiler
        </p>
      </div>
    </div>
  );
};

export default AFAFileUpload;
